// The recipient's form: Formik + yup built from the field specs the open
// endpoint returned. Shared by the direct recipient and the proxy path.
import { useFormik, validateYupSchema, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikRadioGroup } from '@/components/form/fields/FormikRadioGroup/FormikRadioGroup';
import { FormikCheckbox } from '@/components/form/fields/FormikCheckbox/FormikCheckbox';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ResponseErrorType } from '@/types/sharedTypes';
import { TemplateFieldMetaType } from '@/types/vendorVaultTypes';
import { SubmitRecipientProfilePayloadType } from '@/api/v1/recipient-requests/submitRecipientProfileApi';
import { OptionGrid } from '@/bundle/_OrgAdmin/pages/MyVaultPage/styles';
import { WIDE_TEMPLATE_KEYS } from '@/bundle/_OrgAdmin/pages/VendorVaultPage/const/const';
import { ACCOUNT_TYPES_OPTIONS } from '@/bundle/_OrgAdmin/pages/MyVaultPage/const/const';

type TemplateFormType = {
  fields: TemplateFieldMetaType[];
  filledBy: 'recipient' | 'proxy';
  wireConfirmation: boolean;
  isLoading: boolean;
  apiError?: ResponseErrorType;
  onSubmit: (payload: SubmitRecipientProfilePayloadType) => void;
};

type FormValues = {
  fields: Record<string, string>;
  confirmation_channel: '' | 'email' | 'phone';
  confirmation_contact: string;
  save_to_own_vault: boolean;
};

const NORMALIZE_SPACES = ['iban_or_account', 'account_number'];
const UPPER = ['iban_or_account', 'swift', 'currency'];

const schemaFor = (fields: TemplateFieldMetaType[], wireConfirmation: boolean) =>
  Yup.object({
    fields: Yup.object(
      Object.fromEntries(
        fields.map((field) => {
          let rule = Yup.string().trim().max(255, 'Too long');

          if (!field.optional) rule = rule.required(`${field.label} is required.`);
          if (field.pattern) {
            const re = new RegExp(field.pattern);

            rule = rule.test('format', `${field.label} is not in the expected format.`, (value) => {
              if (!value) return field.optional;
              let v = NORMALIZE_SPACES.includes(field.key) ? value.replace(/\s/g, '') : value;

              if (UPPER.includes(field.key)) v = v.toUpperCase();
              if (field.key === 'account_type') v = v.toLowerCase();

              return re.test(v);
            });
          }

          return [field.key, rule];
        })
      )
    ),
    confirmation_contact: wireConfirmation
      ? Yup.string().when('confirmation_channel', {
          is: (v: string) => !!v,
          then: (s) => s.trim().required('Enter where to send the confirmation.'),
        })
      : Yup.string().notRequired(),
  });

export const TemplateForm = ({
  fields,
  filledBy,
  wireConfirmation,
  isLoading,
  apiError,
  onSubmit,
}: TemplateFormType) => {
  const formik = useFormik<FormValues>({
    initialValues: {
      fields: Object.fromEntries(fields.map((f) => [f.key, ''])),
      confirmation_channel: '',
      confirmation_contact: '',
      save_to_own_vault: false,
    },
    validate: async (values) => {
      try {
        await validateYupSchema(values, schemaFor(fields, wireConfirmation));

        return {};
      } catch (error) {
        return yupToFormErrors(error);
      }
    },
    onSubmit: (values) =>
      onSubmit({
        filled_by: filledBy,
        fields: Object.fromEntries(Object.entries(values.fields).map(([k, v]) => [k, (v ?? '').trim()])),
        confirmation_channel: wireConfirmation ? values.confirmation_channel : '',
        confirmation_contact: wireConfirmation ? values.confirmation_contact.trim() : '',
        save_to_own_vault: values.save_to_own_vault,
      }),
  });

  return (
    <FormikForm value={formik}>
      <OptionGrid $cols={2} $gap='0 20px'>
        {fields.map((field) => {
          const formattedLabel = `${field.label}${field.optional ? '' : '*'}`;

          if (field.key === 'account_type') {
            return (
              <div
                key={field.key}
                style={WIDE_TEMPLATE_KEYS.includes(field.key) ? { gridColumn: 'span 2' } : undefined}
              >
                <FormikRadioGroup
                  name={`fields.${field.key}`}
                  label={formattedLabel}
                  options={ACCOUNT_TYPES_OPTIONS}
                  apiError={apiError}
                  hasErrorComponent
                  gridMode
                />
              </div>
            );
          }

          return (
            <div key={field.key} style={WIDE_TEMPLATE_KEYS.includes(field.key) ? { gridColumn: 'span 2' } : undefined}>
              <FormikInput
                name={`fields.${field.key}`}
                label={formattedLabel}
                placeholder={field.placeholder}
                autoComplete='new-field'
                apiError={apiError}
              />
            </div>
          );
        })}
      </OptionGrid>

      {wireConfirmation && (
        <Box marginTop='12px' display='flex' flexDirection='column' rowGap='8px'>
          <FormikRadioGroup
            name='confirmation_channel'
            label={<b>How should they send you the wire confirmation after paying?'</b>}
            options={[
              { value: 'email', label: 'Email' },
              { value: 'phone', label: 'Text message' },
            ]}
          />
          {formik.values.confirmation_channel && (
            <FormikInput
              name='confirmation_contact'
              label={formik.values.confirmation_channel === 'email' ? 'Email address*' : 'Mobile number*'}
              autoComplete='new-field'
              placeholder='Enter your confirmation contact'
            />
          )}
        </Box>
      )}

      <Box marginTop='22px'>
        <FormikCheckbox
          checked={formik.values.save_to_own_vault}
          name='save_to_own_vault'
          label='Also save these details to my own WireVault vault so I can share them again later.'
        />
      </Box>

      <Typography variant='caption' marginTop='18px'>
        Supporting document: bank letter or statement header, only if requested. Upload will be enabled in a later
        release; send it to the requester directly for now.
      </Typography>

      <Box marginTop='16px' justifyContent='flex-end'>
        <ButtonNew size='medium' isLoading={isLoading} onClick={() => formik.handleSubmit()}>
          Submit securely
        </ButtonNew>
      </Box>
    </FormikForm>
  );
};
